import { Injectable, computed, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { VERSION } from '../../../environments/version';

/** Lo que publica /version.json en la raiz del sitio. */
interface VersionPublicada {
  numero: string;
  commit: string;
  compilado: string;
}

/** Lo que devuelve GET /api/v1/version. */
interface VersionDelApi {
  servicio: string;
  version: string;
  commit: string;
  compilado: string;
  ambiente: string;
}

/** Cada cuanto se vuelve a preguntar si el servidor ya tiene algo mas nuevo. */
const INTERVALO_MS = 5 * 60_000;

/**
 * Responde tres preguntas que antes solo se contestaban a ojo:
 *
 *   1. Que version tengo cargada en el navegador  -> `app`, horneada en el bundle.
 *   2. Que version tiene el servidor ahora mismo  -> /version.json, sin cache.
 *   3. Que version trae el API                    -> /api/v1/version.
 *
 * Con (1) contra (2) se sabe si hay que recargar; con (1) contra (3) se detecta el
 * error que mas caro sale: haber desplegado una pieza y la otra no.
 *
 * Nada de esto puede tumbar la pantalla: si una consulta falla, se ignora y se
 * reintenta en la siguiente vuelta. Sin red, la version propia se sigue viendo.
 */
@Injectable({ providedIn: 'root' })
export class VersionService {
  /** La version que este bundle trae dentro. No cambia hasta que se recarga. */
  readonly app = VERSION;

  private readonly _enServidor = signal<VersionPublicada | null>(null);
  private readonly _api = signal<VersionDelApi | null>(null);

  /** Lo que el servidor esta sirviendo ahora. `null` mientras no se sepa. */
  readonly enServidor = this._enServidor.asReadonly();
  /** Version del API. `null` mientras no conteste. */
  readonly api = this._api.asReadonly();

  /** Hay algo mas nuevo publicado que lo que este navegador tiene cargado. */
  readonly hayVersionNueva = computed(() => {
    const s = this._enServidor();
    if (!s) return false;
    return s.numero !== this.app.numero || s.commit !== this.app.commit;
  });

  /** El API y la pantalla no van a la par: se desplego una pieza y la otra no. */
  readonly desajusteConApi = computed(() => {
    const a = this._api();
    if (!a) return false;
    return a.version !== this.app.numero;
  });

  /** Texto corto para mostrar junto al pie: "v1.0.0". */
  readonly etiqueta = computed(() => `v${this.app.numero}`);

  private iniciado = false;

  /** Se llama una sola vez al arrancar la app. */
  iniciar(): void {
    if (this.iniciado) return;
    this.iniciado = true;

    void this.revisar();
    setInterval(() => void this.revisar(), INTERVALO_MS);

    // Al volver a la pestana despues de un rato, preguntar de inmediato.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.revisar();
    });
  }

  /** Vuelve a preguntar por las dos versiones. Nunca lanza. */
  async revisar(): Promise<void> {
    await Promise.all([this.leerServidor(), this.leerApi()]);
  }

  private async leerServidor(): Promise<void> {
    try {
      // Contra document.baseURI, no contra la ruta actual: si no, estando en
      // /catalogos/relojes pediria /catalogos/version.json.
      // El parametro rompe cualquier cache intermedio; no-store rompe el del navegador.
      const url = new URL(`version.json?t=${Date.now()}`, document.baseURI);
      const r = await fetch(url.toString(), { cache: 'no-store' });
      if (!r.ok) return;
      const v = (await r.json()) as VersionPublicada;
      if (v && typeof v.numero === 'string') this._enServidor.set(v);
    } catch {
      // Sin red o sitio a medio desplegar: se reintenta en la siguiente vuelta.
    }
  }

  private async leerApi(): Promise<void> {
    try {
      const r = await fetch(`${environment.apiUrl}/version`, { cache: 'no-store' });
      if (!r.ok) return;
      // El API envuelve toda respuesta en { success, data, ... }.
      const cuerpo = (await r.json()) as { data?: VersionDelApi } | VersionDelApi;
      const v = (cuerpo as { data?: VersionDelApi }).data ?? (cuerpo as VersionDelApi);
      if (v && typeof v.version === 'string') this._api.set(v);
    } catch {
      // El API puede estar reiniciando justo durante un despliegue.
    }
  }
}
